import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import PrimaryButton from '../ui/PrimaryButton';

export default function LandingPage() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-start">
        {/* Background Image */}
        <div className="absolute inset-0 bg-cover bg-center z-0" 
             style={{ backgroundImage: 'url(/images/harbourside_kids.jpg)' }}></div>
        {/* <div className="absolute inset-0 bg-black bg-opacity-20"></div> */}
        
        {/* Content */}
        <div className="relative max-w-6xl mx-auto px-6 text-left" style={{ zIndex: 20 }}>
          <motion.div
            className="bg-white bg-opacity-90 p-8 rounded-lg max-w-lg"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.h1 
              className="text-4xl md:text-5xl font-bold text-dblue mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Welcome to<br />
              <span className="text-yellow-500">Harbourside Children's Center</span>
            </motion.h1>
           <motion.div
              className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="flex items-start gap-3">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-amber-700">
                    North Harbour Families Promotion
                  </p>
                  <p className="mt-1 text-sm text-dblue leading-relaxed">
                    Residents from North Harbour can enjoy a 0$ application fee! Book a tour to secure your spot and take advantage of this exclusive offer.
                  </p>
                </div>
              </div>
            </motion.div>
 
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <PrimaryButton text="Book a tour" href="enrollment" />
            </motion.div>
            
          </motion.div>
        </div>
        
      </section>

      {/* Welcome Section */}
      <section className="py-20 md:py-24 bg-gray-50">
            <div className="max-w-5xl mx-auto px-6">
              <motion.div
                className="max-w-4xl mx-auto"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-dblue mb-10 md:mb-10 text-center">
                  Welcome to Harbourside Children's Centre
                  
                </h2>

                <div className="text-gray-700 text-base md:text-lg leading-8 space-y-3 md:space-y-3 text-left max-w-3xl mx-auto">
                  <p>
                    Nestled in the heart of North Vancouver's Harbourside Community, steps from the North Shore Sea Wall and Lonsdale Quay, Harbourside Children's Centre is where little ones come to learn, laugh, and thrive.
                  </p>

                  <p>
                    We know that choosing the right childcare is one of the most important decisions you'll make as a parent. You need more than just a safe place to drop your child off, you need a team you trust, an environment that inspires, and the confidence that your child is truly happy while you're apart. That's exactly what we set out to provide, every single day.
                  </p>

                  <p>
                    Harbourside Children's Centre believes the early years are the most formative of a child's life. Our experienced, passionate educators create a warm and structured environment where curiosity is encouraged, individuality is celebrated, and every child feels seen, valued, and supported. From the moment your little one walks through our doors, they become part of our community.
                  </p>

                  <p>
                    Located in one of North Vancouver's most beloved neighbourhoods, our centre benefits from the natural beauty and close-knit spirit of the Harbourside community, an environment that reflects the values we bring into our classrooms every day.
                  </p>
                </div>
              </motion.div>
            </div>
          </section>

      {/* Four Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.h3
            className="text-xl font-bold text-dblue pt-4 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Explore What Makes Us Different
          </motion.h3>
          <div className="grid md:grid-cols-4 gap-8">

            {/* Our Classrooms */}
            <motion.div 
              className="text-center p-6"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-dblue" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-dblue mb-2">Our Classrooms</h3>
              <p className="text-gray-600 text-sm">
Bright, age-appropriate learning spaces designed to spark imagination and support developmental milestones.             </p>
            </motion.div>


            {/* Our Environment */}
            <motion.div 
              className="text-center p-6"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2L6 7h2L4 12h4v3H6v3h8v-3h-2v-3h4l-4-5h2l-4-5z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-dblue mb-2">Our Environment</h3>
              <p className="text-gray-600 text-sm">
                A safe, welcoming setting inspired by our beautiful North Shore surroundings, where children feel secure and free to explore.
              </p>
            </motion.div>


            {/* Home Made Meals */}
            <motion.div 
              className="text-center p-6"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5 3a1 1 0 012 0v2a1 1 0 11-2 0V3zm4 0a1 1 0 012 0v2a1 1 0 11-2 0V3zm4 0a1 1 0 012 0v2a1 1 0 11-2 0V3zM3 8a7 7 0 0014 0H3zm1.08 2a7 7 0 0011.84 0H4.08zM8 16h4a1 1 0 110 2H8a1 1 0 110-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-dblue mb-2">Home Made Meals</h3>
              <p className="text-gray-600 text-sm">
 Freshly prepared, nutritious meals made in-house every day because good food is part of good care.
              </p>
            </motion.div>



            {/* Teaching Methods */}
            <motion.div 
              className="text-center p-6"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.65 }}
            >
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-pink-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-dblue mb-2">Teaching Methods</h3>
              <p className="text-gray-600 text-sm">
Play-based, child-led approaches guided by experienced educators who meet each child where they are.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.h3
            className="text-xl font-bold text-dblue mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Your child's best days start here.
          </motion.h3>

          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <PrimaryButton text="Book a Tour" href="/enrollment" />
            <a
              href="tel:6049862388"
              className="inline-flex sm:hidden items-center justify-center bg-dblue text-white px-6 py-3 rounded-full text-lg font-semibold shadow-lg hover:bg-blue-900 transition-colors whitespace-nowrap"
            >
              Call Us Today
            </a>
          </motion.div>
        </div>
      </section>

    </div>

    

  );
}
